import type {
  FinanceContactDto,
  FinanceContactListResponse,
  FinanceContactListParams,
  CreateFinanceContactRequest,
  UpdateFinanceContactRequest,
  FinanceCreateContactResponse,
  SetVoterLinkRequest,
  RecordDonorFollowUpRequest,
  DonorInfoRequestChannel,
} from '@signalvote/shared';
import { failureFromResponse } from '@signalvote/shared';
import { apiJson, apiResponse, apiVoid, type ApiErrorBody } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';

// Re-export types for consumer convenience
export type {
  FinanceContactDto,
  FinanceContactListResponse,
  FinanceContactListParams,
  CreateFinanceContactRequest,
  UpdateFinanceContactRequest,
  FinanceCreateContactResponse,
};

// ============================================================================
// Query Hooks
// ============================================================================

function buildContactListUrl(params: FinanceContactListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.type != null) searchParams.set('type', params.type);
  if (params.search) searchParams.set('search', params.search);
  if (params.trafficStatus) searchParams.set('trafficStatus', params.trafficStatus);
  if (params.donorGiving) searchParams.set('donorGiving', params.donorGiving);
  return `/api/finance/contacts?${searchParams.toString()}`;
}

/** A page of the campaign's donors and payees, with each one's giving totals. */
export const useFinanceContacts = defineQuery({
  key: (params: FinanceContactListParams) => ['finance', 'contacts', 'list', params],
  reads: 'GET /api/finance/contacts',
  fetch: (params, { campaignId }) =>
    apiJson<FinanceContactListResponse>(buildContactListUrl(params), {
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch finance contacts',
    }),
});

/** One contact, or null when it does not exist in this campaign. */
export const useFinanceContact = defineQuery({
  key: (id: string) => ['finance', 'contacts', 'detail', id],
  reads: 'GET /api/finance/contacts/{id}',
  fetch: async (id, { campaignId }) => {
    const response = await apiResponse(`/api/finance/contacts/${id}`, { headers: campaignHeaders(campaignId) });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch finance contact');
    return response.json() as Promise<FinanceContactDto>;
  },
});

/**
 * The server's sentence for a refusal, or `fallback` when it sent none. Not imported from
 * `useFinanceContributions`, which imports this file for the refund's contacts refresh.
 */
const refusal = (fallback: string) => (body: ApiErrorBody, response: Response) =>
  failureFromResponse(response, body, typeof body.error === 'string' && body.error !== '' ? body.error : fallback);

// ============================================================================
// Write Hooks
// ============================================================================

export const useCreateFinanceContact = defineWrite({
  send: (data: CreateFinanceContactRequest) =>
    apiJson<FinanceCreateContactResponse>('/api/finance/contacts', {
      method: 'POST',
      json: data,
      error: refusal('Failed to create contact'),
    }),
});

/**
 * A contact edit. A lost race is `failure.reason.kind === 'stale'`; the body's own token is
 * replaced by the row's.
 */
export const useUpdateFinanceContact = defineWrite({
  row: 'FinanceContact',
  send: (id, concurrencyVersion: number, { data }: { data: UpdateFinanceContactRequest }) =>
    apiJson<FinanceContactDto>(`/api/finance/contacts/${id}`, {
      method: 'PUT',
      json: { ...data, concurrencyVersion },
      error: refusal('Failed to update contact'),
    }),
});

/**
 * Permanently dismiss the person-vs-org shape nudge on a contact whose shape the treasurer
 * verified is correct (POST /api/finance/contacts/{id}/confirm-shape). The endpoint enqueues a
 * ContributorShapeConfirmed audit row keyed on the contact; the derive-time sort-inbox read
 * suppresses BOTH shape nudges ("Looks like a PAC" / "Maybe a person — verify.") for every gift
 * from that donor. Idempotent server-side, so a double-dismiss is harmless.
 */
export const useConfirmContributorShape = defineWrite({
  send: ({ contactId }: { contactId: string }) =>
    apiVoid(`/api/finance/contacts/${contactId}/confirm-shape`, {
      method: 'POST',
      error: refusal('Failed to dismiss the shape reminder'),
    }),
});

export const useLinkContactToVoter = defineWrite({
  row: 'FinanceContact',
  send: (id, concurrencyVersion: number, { voterId }: { voterId: string | null }) =>
    apiJson<FinanceContactDto>(`/api/finance/contacts/${id}/voter-link`, {
      method: 'PUT',
      json: { voterId, concurrencyVersion } satisfies SetVoterLinkRequest,
      error: refusal('Failed to link voter'),
    }),
});

export const useMarkFollowUpSent = defineWrite({
  row: 'FinanceContact',
  send: (id, concurrencyVersion: number, { channel }: { channel: DonorInfoRequestChannel }) =>
    apiJson<FinanceContactDto>(`/api/finance/contacts/${id}/follow-up`, {
      method: 'PUT',
      json: { channel, concurrencyVersion } satisfies RecordDonorFollowUpRequest,
      error: refusal('Failed to record follow-up'),
    }),
});
